import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, errMsg } from "../../lib/api";
import { Plus, Pencil, Trash2, X, Upload, Flame, ImageIcon, FolderPlus } from "lucide-react";

const empty = { categoryId: "", name: "", description: "", price: 0, imageUrl: "", isAvailable: true, isBestSeller: false, stock: 0, trackStock: false, options: [], sortOrder: 0 };

export default function MenuAdmin() {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [newCat, setNewCat] = useState("");

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/categories"), api.get("/products")]);
    setCats(c.data); setProds(p.data);
  };
  useEffect(() => { load(); }, []);

  const addCat = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/categories", { name: newCat.trim(), sortOrder: cats.length + 1 }); setNewCat(""); toast.success("Kategori eklendi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const delCat = async (id) => {
    if (!window.confirm("Kategori ve içindeki ürünler silinsin mi?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Silindi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const delProd = async (id) => {
    if (!window.confirm("Ürün silinsin mi?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Silindi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = filter === "all" ? prods : prods.filter((p) => p.categoryId === filter);
  const catName = (id) => cats.find((c) => c.id === id)?.name || "-";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white">Menü Yönetimi</h1>
          <p className="text-zinc-400 text-sm mt-1">Kategori ve ürünleri yönetin, görselleri buradan yükleyin</p>
        </div>
        <button onClick={() => setEditing({ ...empty, categoryId: cats[0]?.id || "" })} className="btn-gold px-5 py-2.5 rounded-xl flex items-center gap-2" data-testid="add-product-btn"><Plus size={18} /> Ürün Ekle</button>
      </div>

      {/* Categories */}
      <div className="card-lux p-5 mb-6">
        <div className="flex items-center gap-2 mb-4"><FolderPlus size={18} className="text-[#D4AF37]" /><h2 className="font-serif text-xl text-white">Kategoriler</h2></div>
        <div className="flex flex-wrap gap-2 mb-4">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25 text-[#FFE599] text-sm" data-testid={`cat-chip-${c.id}`}>
              {c.name}
              <button onClick={() => delCat(c.id)} className="text-rose-400 hover:text-rose-300" data-testid={`del-cat-${c.id}`}><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="Yeni kategori adı" className="lux-input flex-1" data-testid="new-cat-input" />
          <button onClick={addCat} className="btn-outline px-5 rounded-xl" data-testid="add-cat-btn">Ekle</button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        <button onClick={() => setFilter("all")} className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap ${filter === "all" ? "btn-gold" : "btn-outline"}`}>Tümü ({prods.length})</button>
        {cats.map((c) => <button key={c.id} onClick={() => setFilter(c.id)} className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap ${filter === c.id ? "btn-gold" : "btn-outline"}`}>{c.name}</button>)}
      </div>

      {/* Products */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {shown.map((p) => (
          <div key={p.id} className="card-lux overflow-hidden flex flex-col" data-testid={`admin-prod-${p.id}`}>
            <div className="aspect-[4/3] relative overflow-hidden">
              {p.imageUrl ? (
                <img src={fileUrl(p.imageUrl)} alt={p.name} loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
              ) : (
                <div className="h-full w-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#1b1b20] to-[#0d0d0f]">
                  <ImageIcon className="text-[#D4AF37]/30" size={34} />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]/30">Görsel yok</span>
                </div>
              )}
              {p.isBestSeller && <span className="absolute top-3 left-3 flex items-center gap-1 text-[10px] font-mono px-2.5 py-1 rounded-full bg-[#0B0B0C]/85 backdrop-blur text-[#FFE599] border border-[#D4AF37]/40"><Flame size={11} />Popüler</span>}
              {!p.isAvailable && <span className="absolute top-3 right-3 text-[10px] font-mono px-2.5 py-1 rounded-full bg-rose-950/80 text-rose-300 border border-rose-500/40">Pasif</span>}
            </div>
            <div className="p-4 flex flex-col flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#E5BF47]">{catName(p.categoryId)}</div>
              <h3 className="font-serif text-lg text-white mt-1">{p.name}</h3>
              <p className="text-xs text-zinc-400 mt-1 line-clamp-2 h-8">{p.description}</p>
              <div className="flex items-center justify-between mt-auto pt-3">
                <span className="font-mono text-[#FFE599]">{p.price > 0 ? `₺${p.price.toFixed(2)}` : "Fiyatsız"}</span>
                <div className="flex gap-1.5">
                  <button onClick={() => setEditing(p)} className="btn-outline h-8 w-8 rounded-lg flex items-center justify-center" data-testid={`edit-${p.id}`}><Pencil size={15} /></button>
                  <button onClick={() => delProd(p.id)} className="text-rose-400 hover:bg-rose-500/10 h-8 w-8 rounded-lg flex items-center justify-center" data-testid={`del-prod-${p.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <ProductModal product={editing} cats={cats} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductModal({ product, cats, onClose, onDone }) {
  const [f, setF] = useState(product);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isNew = !product.id;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Yalnızca görsel dosyası yükleyebilirsiniz");
    if (file.size > 25 * 1024 * 1024) return toast.error("Görsel çok büyük (en fazla 25MB). Lütfen daha küçük bir fotoğraf seçin.");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("imageUrl", data.url);
      toast.success("Görsel optimize edilip yüklendi");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.name.trim() || !f.categoryId) return toast.error("Ad ve kategori zorunlu");
    setBusy(true);
    try {
      const payload = { ...f, price: Number(f.price) || 0, stock: Number(f.stock) || 0 };
      if (isNew) await api.post("/products", payload);
      else await api.put(`/products/${f.id}`, payload);
      toast.success(isNew ? "Ürün eklendi" : "Güncellendi");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="product-modal">
      <div className="card-lux w-full max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar scale-in">
        <div className="px-5 py-4 border-b border-[#D4AF37]/20 flex items-center justify-between sticky top-0 bg-[#121215] z-10">
          <h2 className="font-serif text-2xl gold-text">{isNew ? "Yeni Ürün" : "Ürünü Düzenle"}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="product-modal-close"><X size={24} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-24 w-24 rounded-xl bg-[#18181C] overflow-hidden flex items-center justify-center shrink-0">
              {f.imageUrl ? <img src={fileUrl(f.imageUrl)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="text-[#D4AF37]/30" size={30} />}
            </div>
            <label className="btn-outline px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 cursor-pointer" data-testid="upload-image-label">
              <Upload size={16} /> {uploading ? "Yükleniyor..." : "Görsel Yükle"}
              <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="upload-image-input" />
            </label>
          </div>
          <p className="text-[11px] text-zinc-500 -mt-2">Büyük fotoğraflar otomatik küçültülür (en fazla 1600px, WEBP). Telefon fotoğrafı yükleyebilirsiniz — sistem hafifletir.</p>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Ürün Adı</label>
            <input value={f.name} onChange={(e) => set("name", e.target.value)} className="lux-input mt-2" data-testid="product-name" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Kategori</label>
            <select value={f.categoryId} onChange={(e) => set("categoryId", e.target.value)} className="lux-input mt-2" data-testid="product-category">
              <option value="">Seçiniz</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Açıklama / İçindekiler</label>
            <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} className="lux-input mt-2" data-testid="product-desc" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Fiyat (₺)</label>
              <input type="number" step="0.5" value={f.price} onChange={(e) => set("price", e.target.value)} className="lux-input mt-2" data-testid="product-price" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Stok</label>
              <input type="number" value={f.stock} onChange={(e) => set("stock", e.target.value)} className="lux-input mt-2" data-testid="product-stock" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="toggle-available"><input type="checkbox" checked={f.isAvailable} onChange={(e) => set("isAvailable", e.target.checked)} className="accent-[#D4AF37] w-4 h-4" /> Satışta</label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="toggle-bestseller"><input type="checkbox" checked={f.isBestSeller} onChange={(e) => set("isBestSeller", e.target.checked)} className="accent-[#D4AF37] w-4 h-4" /> Popüler (Öne çıkar)</label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="toggle-trackstock"><input type="checkbox" checked={f.trackStock} onChange={(e) => set("trackStock", e.target.checked)} className="accent-[#D4AF37] w-4 h-4" /> Stok Takibi</label>
          </div>
        </div>
        <div className="p-5 border-t border-[#D4AF37]/20 flex gap-3">
          <button onClick={onClose} className="btn-outline flex-1 py-3 rounded-xl">İptal</button>
          <button onClick={save} disabled={busy} className="btn-gold flex-1 py-3 rounded-xl" data-testid="product-save">{busy ? "..." : "Kaydet"}</button>
        </div>
      </div>
    </div>
  );
}
